import type { FC } from 'react'
import type { TwStyle } from 'twin.macro'
import type { MotionProps } from 'framer-motion'
import type { Atom } from 'jotai'
import type { SalesData } from '@/features/pwNftSale/atoms/salesDataAtom'

import React, { useState, useEffect } from 'react'
import tw, { styled } from 'twin.macro'
import { motion } from 'framer-motion'
import Link from 'next/link'
import { Progress, } from '@chakra-ui/react'
import { useAtomValue } from 'jotai'

import {useNamedBoundingRectRef, useProgressControl} from '@/components/Tour'
import { SaleProgressCircle } from '@/components/SalesProgress'
import { LevelEnum } from '@/types/nftAttribute'
import { legendarySalesDataAtom, magicSalesDataAtom, primeSalesDataAtom } from '@/features/pwNftSale/atoms/salesDataAtom'
import * as nftSaleConstants from '@/features/pwNftSale/constants'

import AiSpectreIcon from '../../../svgs/species-aispectre.svg' 
import CyborgIcon from '../../../svgs/species-cyborg.svg' 
import PandroidIcon from '../../../svgs/species-pandroid.svg' 
import XGeneIcon from '../../../svgs/species-xgene.svg' 

const RoleImg = tw.img`h-auto block relative left-1/2 -translate-x-1/2`

const LightRole = styled.div<{ rarity: string }>`
  ${tw`absolute top-0 bottom-0 left-0 right-0 transition-all duration-700`}
  ${tw`opacity-0 hover:opacity-100 bg-no-repeat bg-bottom`}
  ${({ rarity }) => `background-image: url('/static/images/indexPage/rarity/Hover_Light/${rarity}.png');`}

  img {
    ${tw`absolute h-auto left-1/2 -translate-x-1/2`}
  }
`

type Preset = Required<Pick<MotionProps, 'initial' | 'animate' | 'exit'>> & {
  canvasPosition?: TwStyle,
  haloPosition: TwStyle,
  highlightPosition?: TwStyle,
}

const presets: Readonly<Record<LevelEnum, Preset>> = {
  [LevelEnum.Common]: {
    initial: {opacity: 0, left: '50vw'},
    animate: {opacity: 1, left: 'auto'},
    exit: {opacity: 0, left: '50vw'},
    canvasPosition: tw`bottom-[-0.5%]`,
    haloPosition: tw`bottom-[-12%] left-[-20%] w-[140%]`,
    highlightPosition: tw`w-[140%] left-[-20%]`,
  },
  [LevelEnum.Magic]: {
    initial: {opacity: 0, top: '110vh'},
    animate: {opacity: 1, top: 'auto'},
    exit: {opacity: 0, top: '100vh'},
    canvasPosition: tw`bottom-[0.5%]`,
    haloPosition: tw`bottom-[-8%] left-[-20%] w-[140%]`,
    highlightPosition: tw`w-[120%] left-[-10%]`,
  },
  [LevelEnum.Legendary]: {
    initial: {opacity: 0, left: '-50vw'},
    animate: {opacity: 1, left: 'auto'},
    exit: {opacity: 0, left: '-50vw'},
    haloPosition: tw`bottom-[-6%] left-[-6%] w-[112%]`,
  },
}

interface CatalogItemProps {
  preset: Preset,
  colorScheme: string,
  salesDataAtom: Atom<SalesData>
  announcement: string
  title: string
}

const CatalogItem: FC<CatalogItemProps> = ({ preset, colorScheme, salesDataAtom, announcement, title }) => {
  const [hover, setHover] = useState(false)
  const salesData = useAtomValue(salesDataAtom)
  return (
    <Link href={`/rarities/${colorScheme}`} legacyBehavior>
      <motion.div
        tw="flex flex-col gap-2.5 cursor-pointer"
        initial={preset.initial}
        animate={preset.animate}
        exit={preset.exit}
        transition={{duration: 0.7, ease: 'easeInOut'}}
        onHoverStart={() => setHover(true)}
        onHoverEnd={() => setHover(false)}
      >
        <div
          css={[
            tw`w-full flex flex-row gap-4 justify-center opacity-0 transition-all duration-500`,
            hover ? tw`opacity-100` : null
          ]}
        >
          <SaleProgressCircle
            colorScheme={colorScheme}
            sold={
              (salesData.open && colorScheme === 'prime') ? (salesData.skus['AI Spectre'].sold + salesData.skus['AI Spectre'].preOrdered) : salesData.skus['AI Spectre'].sold
            }
            max={salesData.skus['AI Spectre'].total}
          >
            <AiSpectreIcon tw="h-6 w-6" />
          </SaleProgressCircle>
          <SaleProgressCircle
            colorScheme={colorScheme}
            sold={
              (salesData.open && colorScheme === 'prime') ? (salesData.skus['Cyborg'].sold + salesData.skus['Cyborg'].preOrdered) : salesData.skus['Cyborg'].sold
            }
            max={salesData.skus['Cyborg'].total}
          >
            <CyborgIcon tw="h-6 w-6" />
          </SaleProgressCircle>
          <SaleProgressCircle
            colorScheme={colorScheme}
            sold={
              (salesData.open && colorScheme === 'prime') ? (salesData.skus['Pandroid'].sold + salesData.skus['Pandroid'].preOrdered) : salesData.skus['Pandroid'].sold
            }
            max={salesData.skus['Pandroid'].total}
          >
            <PandroidIcon tw="h-6 w-6" />
          </SaleProgressCircle>
          <SaleProgressCircle
            colorScheme={colorScheme}
            sold={
              (salesData.open && colorScheme === 'prime') ? (salesData.skus['X-Gene'].sold + salesData.skus['X-Gene'].preOrdered) : salesData.skus['X-Gene'].sold
            }
            max={salesData.skus['X-Gene'].total}
          >
            <XGeneIcon tw="h-6 w-6" />
          </SaleProgressCircle>
        </div>
        <div tw="flex-grow flex flex-col items-center relative justify-end gap-10">
          <div css={[tw`relative`, preset.canvasPosition]}>
            <img
              css={[
                tw`absolute block h-auto max-w-none`,
                preset.haloPosition,
                ((salesData.open || salesData.salePhase === nftSaleConstants.SalePhases.CLOSED) || hover) ? null : tw`grayscale-[100] brightness-200`,
              ]}
              src={`/static/images/indexPage/rarity/circle/${colorScheme}.png`}
              alt=""
            />
            <RoleImg
              css={[
                (salesData.open || salesData.salePhase === nftSaleConstants.SalePhases.CLOSED) ? null : tw`grayscale-[100] brightness-200`
              ]}
              src={`/static/images/indexPage/rarity/role/dark/${colorScheme}.png`}
              alt=""
            />
            <LightRole
              css={[preset.highlightPosition, hover ? tw`opacity-100` : null]}
              rarity={colorScheme}
            >
              <img
                src={`/static/images/indexPage/rarity/role/light/${colorScheme}.png`}
                alt=""
              />
            </LightRole>
          </div>
        </div>
        <div tw="flex flex-col gap-2.5 items-center">
          <h2 tw="mt-5 font-secondary font-extrabold text-xl text-white uppercase">{title}</h2>
          <div>
            {salesData.open && (
              <div tw="w-[12.5vw] bg-grey-300 rounded mb-4">
                <Progress size="sm" colorScheme={colorScheme} value={salesData.sold} max={salesData.total} />
              </div>
            )}
            {!salesData.end &&  (
              <p>{announcement}</p>
            )}
          </div>
          <div tw="h-8">
            {salesData.soldout ? (
              <p tw="text-red-500 text-base">SOLD OUT</p>
            ) : (
              salesData.end ? (
                <p tw="text-red-500 text-base">End Of Sale</p>
              ) : (
                <p tw="font-primary text-sm text-[rgba(255,255,255,0.5)]">{salesData.summary}</p>
              )
            )}
          </div>
        </div>
      </motion.div>
    </Link>
  );
}

const formatter = new Intl.DateTimeFormat('en-US')

export default function SpeciesCatalog() {
  // const imageList = preloadImageList.map((item) => tryWebp(item))
  // const isImageLoading = useImageLoader(imageList as string[])
  const ref = useNamedBoundingRectRef<HTMLDivElement>('chooseRarityHint')
  const dispatch = useProgressControl('chooseRarityHint')
  useEffect(() => {
    let seed: ReturnType<typeof setTimeout> | undefined
    seed = setTimeout(() => {
      dispatch('next')
    }, 2000)
    return () => {
      dispatch('cancel')
      if (seed) {
        clearTimeout(seed)
      }
    }
  }, [dispatch])
  return (
    <div tw="flex flex-row justify-center gap-40" ref={ref}>
      <CatalogItem
        preset={presets[LevelEnum.Legendary]}
        colorScheme='legendary'
        salesDataAtom={legendarySalesDataAtom}
        announcement={`Coming soon on 31st Oct. 8PM`}
        title={LevelEnum.Legendary}
      />
      <CatalogItem
        preset={presets[LevelEnum.Magic]}
        colorScheme='magic'
        salesDataAtom={magicSalesDataAtom}
        announcement={`Coming soon on 31st Oct. 8PM`}
        title={LevelEnum.Magic}
      />
      <CatalogItem
        preset={presets[LevelEnum.Common]}
        colorScheme='prime'
        salesDataAtom={primeSalesDataAtom}
        announcement={`Coming soon on 2nd Nov. 8PM`}
        title={LevelEnum.Common}
      />
      {/* {isImageLoading && <Loading />} */}
    </div>
  )
}
